import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingBag, Plus, Minus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, fmtErr } from "../lib/api";

const parsePrice = (p) => parseFloat(String(p || "").replace(/[^0-9.]/g, "")) || 0;

export default function OrderOnline() {
  const { id } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [menus, setMenus] = useState([]);
  const [cart, setCart] = useState({});
  const [orderType, setOrderType] = useState("collection");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/restaurants/${id}`).then((r) => {
      setRestaurant(r.data);
      api.get(`/restaurants/${r.data.id}/menus`).then((m) => setMenus(m.data)).catch(() => {});
    }).catch(() => {});
  }, [id]);

  if (restaurant && !(restaurant.online_ordering || {}).enabled) {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="ordering-disabled">
        <p className="text-muted-foreground">Online ordering isn't available at {restaurant.name} right now.</p>
        <Link to={`/restaurants/${restaurant.slug || restaurant.id}`}><Button className="mt-6 rounded-full">Book a table instead</Button></Link>
      </div>
    );
  }
  if (!restaurant) return <div className="min-h-screen bg-background" />;

  const items = Object.values(cart);
  const total = items.reduce((n, i) => n + parsePrice(i.price) * i.qty, 0);
  const deliveryOn = (restaurant.online_ordering || {}).delivery;

  const add = (it) => setCart((c) => ({ ...c, [it.name]: { name: it.name, price: it.price, qty: (c[it.name]?.qty || 0) + 1 } }));
  const dec = (name) => setCart((c) => {
    const next = { ...c };
    const q = (next[name]?.qty || 0) - 1;
    if (q <= 0) delete next[name]; else next[name] = { ...next[name], qty: q };
    return next;
  });

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/online-orders`, {
        ...form, order_type: orderType,
        items: items.map((i) => ({ name: i.name, price: i.price, qty: i.qty })),
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setDone(data);
      toast.success("Order sent to the kitchen");
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="order-confirmed">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h1 className="font-display text-3xl mt-6">Order received</h1>
        <p className="text-muted-foreground mt-3 text-sm">
          {restaurant.name} has your {done ? "order" : ""} — pay on {orderType}.<br />
          Order ref: <span className="text-foreground">{done.order_id.slice(0, 8)}</span>
        </p>
        <Link to={`/restaurants/${restaurant.slug || restaurant.id}`}><Button variant="outline" className="mt-8 rounded-full">Back to {restaurant.name}</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="order-online-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 min-h-screen">
      <p className="text-xs tracking-[0.3em] uppercase text-primary mb-2">Collection & delivery</p>
      <h1 className="font-display text-4xl tracking-tight">Order from {restaurant.name}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
        <div className="lg:col-span-8 space-y-8">
          {menus.map((m) => (
            <div key={m.id}>
              <h2 className="font-outfit font-medium text-xl mb-4">{m.name}</h2>
              {m.sections.map((s, si) => (
                <div key={si} className="mb-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">{s.title}</p>
                  <div className="space-y-2.5">
                    {s.items.map((it, ii) => (
                      <div key={ii} className="flex items-center justify-between rounded-xl border border-border/60 bg-card px-5 py-3.5">
                        <div>
                          <p className="text-sm font-medium">{it.name}</p>
                          {it.description && <p className="text-xs text-muted-foreground mt-0.5">{it.description}</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-outfit text-sm">{it.price}</span>
                          {cart[it.name] ? (
                            <div className="flex items-center gap-2">
                              <button data-testid={`dec-${it.name.replace(/\s+/g, "-")}`} onClick={() => dec(it.name)}
                                className="w-7 h-7 rounded-full border border-border flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                              <span className="text-sm w-4 text-center">{cart[it.name].qty}</span>
                              <button data-testid={`inc-${it.name.replace(/\s+/g, "-")}`} onClick={() => add(it)}
                                className="w-7 h-7 rounded-full border border-primary/60 text-primary flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <Button data-testid={`add-${it.name.replace(/\s+/g, "-")}`} size="sm" variant="outline" className="rounded-full h-8" onClick={() => add(it)}>Add</Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {menus.length === 0 && <p className="text-muted-foreground">Menu coming soon.</p>}
        </div>

        <div className="lg:col-span-4">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sticky top-24" data-testid="order-cart">
            <h3 className="font-outfit font-medium text-lg flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-primary" /> Your order</h3>
            <div className="flex gap-2 mt-4">
              <button data-testid="order-type-collection" onClick={() => setOrderType("collection")}
                className={`flex-1 rounded-full border px-4 py-2 text-sm transition-colors ${orderType === "collection" ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground"}`}>
                Collection
              </button>
              <button data-testid="order-type-delivery" onClick={() => deliveryOn && setOrderType("delivery")}
                className={`flex-1 rounded-full border px-4 py-2 text-sm transition-colors ${orderType === "delivery" ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground"} ${!deliveryOn ? "opacity-40 cursor-not-allowed" : ""}`}>
                Delivery
              </button>
            </div>
            <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
              {items.map((i) => (
                <div key={i.name} className="flex justify-between text-sm">
                  <span>{i.qty}× {i.name}</span>
                  <span className="text-muted-foreground">£{(parsePrice(i.price) * i.qty).toFixed(2)}</span>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-muted-foreground">Cart is empty.</p>}
            </div>
            <div className="flex justify-between border-t border-border/60 mt-4 pt-3">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-outfit font-semibold text-lg" data-testid="order-total">£{total.toFixed(2)}</span>
            </div>
            <div className="mt-4 space-y-2.5">
              <Input data-testid="order-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-border/60" />
              <Input data-testid="order-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border-border/60" />
              <Input data-testid="order-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border-border/60" />
              {orderType === "delivery" && (
                <Input data-testid="order-address" placeholder="Delivery address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-background border-border/60" />
              )}
              <Input data-testid="order-notes" placeholder="Notes / allergies (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-border/60" />
              <Button data-testid="place-order-btn" onClick={submit}
                disabled={submitting || items.length === 0 || !form.name || !form.email || (orderType === "delivery" && !form.address)}
                className="w-full rounded-full">
                {submitting ? "Placing order..." : restaurant.stripe_connected ? `Pay £${total.toFixed(2)}` : `Place order · pay on ${orderType}`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
